import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    getAuditDataOfFolder,
    getPurchasesOfFolder,
    getFileInfo
} from '../../Services/Search.services';
import { toast } from 'react-toastify';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import SkeletonComponent from '../SkeletonComponent';
import { datetimeFormat } from '../../Constants/Date.constant';
import { AvlTable } from './Table/Table';
import { UrlActionType, UrlButton } from './UrlButton/UrlButton';
import { format } from 'date-fns';
import { dateFormat } from '../../Constants/Date.constant';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PurchasesTable from './PurchasesTable';
import { getFolderTitleNumbers } from '../../Services/Search.services';

const columns = {
    performedAt: 'Performed At',
    details: 'Details',
    app: 'App',
    username: 'Performed By',
    operation: 'Operation',
    status: 'Status',
};

const convertPythonToJson = (str) => {
    return str
        .replace(/'/g, '"')          
        .replace(/\bNone\b/g, 'null')      
        .replace(/\bTrue\b/g, 'true')      
        .replace(/\bFalse\b/g, 'false');   
};

const parseJsonObject = (trimmed, documents) => {
    const jsonString = convertPythonToJson(trimmed);
    const parsed = JSON.parse(jsonString);
    
    const documentId = Array.isArray(parsed) ? parsed[0] : parsed.doc_id;
    const document = documents.find((doc) => doc.document_id === documentId);
    if (!document) return parsed;
    
    return { 
        documentId, 
        type: document.file_type, 
        title: document.title,
        isError: document.is_error,
        uploadedAt: format(new Date(document.uploaded_at), datetimeFormat)
    };
};

const parseKeyValuePairs = (trimmed) => {
    const obj = {};
    
    trimmed.split('\n').forEach((line) => {
        if (!line.includes(':')) return;

        const index = line.indexOf(':');
        const key = line.slice(0, index).trim();
        const value = line.slice(index + 1).trim();

        if (key) obj[key] = value;
    });

    return Object.keys(obj).length ? obj : null;
};

function FolderAudits({ folderId }) {
    const [records, setRecords] = useState([]);
    const [purchases, setPurchases] = useState([]);
    const [pageCount, setPageCount] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [countRowsPerPage, setCountRowsPerPage] = useState(20);
    const [totalRows, setTotalRows] = useState(20);
    const [documents, setDocuments] = useState([]);
    const [textSearch, setTextSearch] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [titleNumbers, setTitleNumbers] = useState([]);
    const [selectedTitleNumber, setSelectedTitleNumber] = useState('');
    const [isSelectOpen, setIsSelectOpen] = useState(false);

    useEffect(() => {
        getTitleNumbers();
    }, []);

    useEffect(() => {
        const handleResize = () => setIsSelectOpen(false);

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setPage(1);
        getRecords(1, countRowsPerPage);
    }, [selectedTitleNumber]);

    const getFilterFormValue = (filterValues) => {
        const startDate = filterValues.startDate;
        const endDate = filterValues.endDate;
        const formattedStartDate = startDate && format(startDate, dateFormat);
        const formattedEndDate = endDate && format(endDate, dateFormat);

        setStartDate(formattedStartDate);
        setEndDate(formattedEndDate);
        setTextSearch(filterValues.text);
        setPage(1);
    };

    const sortFolderAudits = (audits) => {
        return audits.sort((a, b) => {
            const aTimestamp = new Date(a.performed_at).getTime();
            const bTimestamp = new Date(b.performed_at).getTime();

            if (aTimestamp === bTimestamp) {
                return -1;
            }

            return aTimestamp - bTimestamp;
        });
    };

    const fetchDocumentsInfo = async () => {
        try {
            const { folders } = await getFileInfo(folderId);
            setDocuments(folders);
        } catch (error) {
            toast.error('Failed to load documents. Please try again later.');
        }
    };

    const getRecords = async (page, rowsPerPage) => {
        setIsLoading(true);
        rowsPerPage ??= countRowsPerPage;
        const urlParam = makeParams(page, rowsPerPage);

        getAuditDataOfFolder(folderId, urlParam)
            .then((response) => {
                const rowsCount = Number(response.data.count);
                const limit = Number(response.data.limit);
                const pagesCount = Math.ceil(rowsCount / limit);

                setTotalRows(rowsCount);
                setRecords(sortFolderAudits(response.data['folder_audit']));
                setPageCount(pagesCount);
            })
            .finally(() => setIsLoading(false))
            .catch(() => {
                setRecords([]);
                setPageCount(1);
            });

        getPurchasesOfFolder(folderId)
            .then((response) => {
                setPurchases(response.purchases);
            })
            .catch(() => {
                setPurchases([]);
            });
    };

    const getTitleNumbers = async () => {
        try {
            const data = await getFolderTitleNumbers(folderId);
            setTitleNumbers(data?.title_numbers || []);
        } catch (error) {
            setTitleNumbers([]);
        }
    };

    const handleReset = () => {
        setSelectedTitleNumber('');
    };

    const handlePageChange = (event, value) => {
        setIsLoading(true);
        setPage(value);
        getRecords(value);
    };

    const handleChangeRowsPerPagePage = (rowsPerPage) => {
        const initialPage = 1;
        setCountRowsPerPage(rowsPerPage);
        setPage(initialPage);
        getRecords(initialPage, rowsPerPage);
    };

    const makeParams = (paginationPage, rowsPerPage) => {
        let urlParam = '?limit=' + rowsPerPage;

        if (paginationPage) {
            urlParam += '&offset=' + (paginationPage - 1) * rowsPerPage;
        }

        if (selectedTitleNumber) {
            urlParam += '&query=' + encodeURIComponent(selectedTitleNumber);
        }

        return urlParam;
    };

    const tableHeaders = [
        {
            label: columns.performedAt,
            align: 'left',
        },
        {
            label: columns.operation,
            align: 'left',
        },
        {
            label: columns.details,
            align: 'left',
        },
        {
            label: columns.username,
            align: 'left',
        },
        {
            label: columns.status,
            align: 'center',
        },
    ];

    const parseExtra = (extra) => {
        if (!extra || typeof extra !== 'string') return null;
    
        const trimmed = extra.trim();

        const isArray = trimmed.startsWith('[') && trimmed.endsWith(']');
        const isObject = trimmed.startsWith('{') && trimmed.endsWith('}');

        if (isArray || isObject) return parseJsonObject(trimmed, documents);
    
        if (trimmed.includes(':')) {
            const parsed = parseKeyValuePairs(trimmed);
            if (parsed) return parsed;
        }
    
        return trimmed;
    };

    const renderExtra = (extra) => {
        const parsed = parseExtra(extra);
    
        if (!parsed) return <span>-</span>;
    
        if (typeof parsed === 'object') {
            return (
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '150px 1fr',
                    columnGap: '16px',
                    rowGap: '8px',
                    fontSize: '14px'
                }}>
                    {Object.entries(parsed).map(([key, value]) => (
                        <div key={key} style={{ display: 'contents' }}>
                            <div style={{ 
                                fontWeight: '700',
                                wordBreak: 'break-word'
                            }}>
                                {key}
                            </div>
                            <div style={{ 
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'break-word'
                            }}>
                                {String(value) || '-'}
                            </div>
                        </div>
                    ))}
                </div>
            );
        }
    
        return <span>{parsed}</span>;
    };


    // Group purchase data by reference
    const purchases_by_reference = purchases?.reduce((acc, current) => {
        const { reference, ...rest } = current;
        if (!acc[reference]) {
            acc[reference] = [];
        }
        acc[reference].push(current);
        return acc;
    }, {});

    const purchases_for_references = (references) => {
        return references?.flatMap((reference) => {
            return purchases_by_reference[reference] || [{ reference }];
        });
    };

    const convertToCells = (row, rowIndex) => [
        {
            style: { minWidth: '90px' },
            data: row.performed_at
                ? format(new Date(row.performed_at), datetimeFormat)
                : 'None',
        },
        {
            data: row.operation || 'N/A',
        },
        {
            align: 'left',
            style: { minWidth: '350px', maxWidth: '700px' },
            data:
                row?.purchases?.length > 0 ? (
                    <Accordion key={row.reference}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="stats-content"
                            id="stats-header"
                        >
                            <Typography>PURCHASES</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <PurchasesTable
                                records={purchases_for_references(
                                    row?.purchases?.map(
                                        (item) => item.reference
                                    )
                                )}
                                isLoading={false}
                                rowsPerPageDefault={5}
                            />
                        </AccordionDetails>
                    </Accordion>
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div style={{ overflowWrap: 'break-word', whiteSpace: 'pre-line' }}>
                            {renderExtra(row.extra)}
                        </div>
                    </div>
                ),
        },
        {
            style: { minWidth: '90px' },
            data: !row.user_name ? (
                'N/A'
            ) : (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                        style={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            maxWidth: '220px',
                        }}
                    >
                        {row.user_name}
                    </div>
                    <UrlButton
                        buttonStyles={{
                            link: { width: '32px', height: '32px' },
                        }}
                        description={`Copy to clipboard "${row.user_name}"`}
                        action={UrlActionType.copyToClipboard}
                        url={row.user_name}
                    />
                </div>
            ),
        },
        {
            align: 'center',
            style: { width: '80px' },
            data: row.status || 'None',
        },
    ];

    const tableRows = records.map((row, rowIndex) => ({
        cells: convertToCells(row, rowIndex),
    }));

    return (
        <div
            style={{
                padding: '10px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                <FormControl size="small" style={{ flex: 1 }}>
                    <InputLabel>Title Number</InputLabel>
                    <Select
                        value={selectedTitleNumber}
                        label="Title Number"
                        open={isSelectOpen}
                        onOpen={() => setIsSelectOpen(true)}
                        onClose={() => setIsSelectOpen(false)}
                        onChange={(e) => setSelectedTitleNumber(e.target.value)}
                        MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                    >
                        {titleNumbers.length === 0
                            ? <MenuItem disabled>No title numbers found</MenuItem>
                            : titleNumbers.map((tn) => (
                                <MenuItem 
                                    key={tn} 
                                    value={tn}
                                >
                                    {tn}
                                </MenuItem>
                            ))
                        }
                    </Select>
                </FormControl>
                <Button 
                    variant="outlined" 
                    onClick={handleReset} 
                    style={{ flexShrink: 0 }}
                >
                    RESET
                </Button>
            </div>
            {isLoading ? (
                <SkeletonComponent />
            ) : records.length > 0 ? (
                <AvlTable
                    headers={tableHeaders}
                    rows={tableRows}
                    page={page}
                    pageCount={pageCount}
                    isRowsPerPageOptionsVisible
                    onPageChange={handlePageChange}
                    rowsPerPage={countRowsPerPage}
                    rowsCount={totalRows}
                    onChangeRowsPerPage={handleChangeRowsPerPagePage}
                />
            ) : (
                <Stack sx={{ width: '100% !important' }} spacing={2}>
                    <Alert severity="warning">Records not found!</Alert>
                </Stack>
            )}
        </div>
    );
}

FolderAudits.propTypes = {
    folderId: PropTypes.string.isRequired,
};
export default FolderAudits;
